import { useState } from 'react';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { GlobalSearchHitSchema } from '@taqon/contracts';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { z } from 'zod';
import { getData } from '../../shared/api/client';
import { recordPath } from '../../shared/records/routes';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { isSearchable } from './searchable';

/**
 * Global search results (FRD-CORE-038, CORE-039): the records the user may see, across modules,
 * best matches first. Each opens its own page, which enforces its own permission again; a record
 * type without a page of its own is named but not linked. The query is in the address, so results
 * survive a refresh and can be shared.
 */
export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = (params.get('q') ?? '').trim();
  const searchable = isSearchable(q);
  const [draft, setDraft] = useState(q);
  // A new search from the header box while this page is open shows its words here too.
  const [shown, setShown] = useState(q);
  if (shown !== q) {
    setShown(q);
    setDraft(q);
  }
  const results = useQuery({
    queryKey: ['search', q],
    enabled: searchable,
    queryFn: () =>
      getData(`/api/v1/search?q=${encodeURIComponent(q)}&limit=50`, z.array(GlobalSearchHitSchema)),
  });
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Search"
        description="Employees, positions, organization masters, suppliers, purchase requests and orders, clients, contacts, activities, projects, assets, leads, campaigns and migration batches: only the records you may see."
      />
      <Stack
        component="form"
        role="search"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        onSubmit={(e) => {
          e.preventDefault();
          if (isSearchable(draft)) setParams({ q: draft.trim() });
        }}
      >
        <TextField
          label="Search for"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          sx={{ minWidth: { sm: 360 } }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <Button type="submit" variant="contained" disabled={!isSearchable(draft)}>
          Search
        </Button>
      </Stack>
      <Section title={q === '' ? 'Results' : `Results for “${q}”`}>
        {!searchable && <EmptyState title="Type at least two letters or digits" />}
        {searchable && results.isPending && <LoadingState label="Searching" />}
        {searchable && results.isError && <ErrorState message={results.error.message} />}
        {searchable && results.data?.length === 0 && (
          <EmptyState
            title="Nothing found"
            detail="Only records you are allowed to see are searched."
          />
        )}
        {searchable && results.data !== undefined && results.data.length > 0 && (
          <Table size="small" aria-label="Search results">
            <TableHead>
              <TableRow>
                <TableCell>Record</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.data.map((hit) => {
                const path = recordPath(hit.recordType, hit.recordId);
                return (
                  <TableRow key={`${hit.recordType}:${hit.recordId}`} hover>
                    <TableCell>
                      {path === null ? (
                        hit.title
                      ) : (
                        <Link component={RouterLink} to={path} sx={{ fontWeight: 600 }}>
                          {hit.title}
                        </Link>
                      )}
                      {hit.subtitle !== null && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="span"
                          sx={{ display: 'block' }}
                        >
                          {hit.subtitle}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{hit.recordTypeName}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Section>
    </Stack>
  );
}
